import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet, Svg, Circle, Path } from '@react-pdf/renderer';
import { calculateEtiquetteDPE } from '@/utils/dpe-projections';
import { registerPDFFonts } from '@/utils/pdf-fonts';

registerPDFFonts();

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

interface Inconsistency {
  type: string;
  severity: 'CRITIQUE' | 'MAJEURE' | 'INFORMATION';
  suspected_error?: string;
  dpe_value?: string;
  user_reality?: string;
  user_value?: string;
  recommendation?: string;
  impact?: string;
}

interface WorkAction {
  id: string;
  title: string;
  description?: string;
  category?: string;
  priority?: string;
  difficulty?: string;
  estimated_savings?: number;
  estimated_cost?: number;
}

interface AnalysisPDFDocumentProps {
  companyName: string;
  logoUrl: string | null;
  primaryColor?: string;
  dpeData: any;
  sessionData?: any;
  confidenceScore: number;
  rulesAppliedCount?: number;
  inconsistenciesDetected: Inconsistency[];
  immediateActions?: WorkAction[];
  shortTermActions?: WorkAction[];
  hasCollectiveSystem?: boolean;
  collectiveSystemWarning?: any;
  analysisDate?: string;
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const getDPEColor = (cls: string): string => {
  const colors: Record<string, string> = {
    A: '#009966', B: '#33CC33', C: '#CCFF00',
    D: '#FFFF00', E: '#FFCC00', F: '#FF9900', G: '#FF0000',
  };
  return colors[cls?.toUpperCase()] ?? '#9CA3AF';
};

// Couleur du texte sur fond DPE (C/D/E ont fond clair)
const getDPETextColor = (cls: string): string => {
  return ['C', 'D', 'E'].includes(cls?.toUpperCase()) ? '#1F2937' : '#FFFFFF';
};

const getSeverityColor = (severity: string) => {
  if (severity === 'CRITIQUE') return { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B', badge: '#EF4444', badgeText: '#FFFFFF' };
  if (severity === 'MAJEURE') return { bg: '#FFF7ED', border: '#F97316', text: '#9A3412', badge: '#F97316', badgeText: '#FFFFFF' };
  return { bg: '#EFF6FF', border: '#3B82F6', text: '#1E40AF', badge: '#DBEAFE', badgeText: '#1D4ED8' };
};

const getSeverityLabel = (severity: string) => {
  if (severity === 'CRITIQUE') return 'CRITIQUE';
  if (severity === 'MAJEURE') return 'MAJEURE';
  return 'PRÉCISION';
};

const getPriorityColor = (priority?: string) => {
  if (priority === 'HAUTE' || priority === 'CRITIQUE') return { bg: '#FFF7ED', text: '#EA580C' };
  if (priority === 'MOYENNE') return { bg: '#FFFBEB', text: '#D97706' };
  return { bg: '#F0FDF4', text: '#16A34A' };
};

const getInconsistencyTitle = (type: string): string => {
  const titles: Record<string, string> = {
    VOLETS_INCONSISTENCY: 'Protection solaire des menuiseries (volets, stores)',
    VENTILATION_INCONSISTENCY: 'Système de ventilation',
    VENTILATION_CLARIFICATION: "Opportunité d'optimisation ventilation",
    ISOLATION_INCONSISTENCY: 'Isolation thermique',
    SURFACE_INCONSISTENCY: 'Surface habitable',
    HAUTEUR_INCONSISTENCY: 'Hauteur sous plafond',
    EQUIPEMENT_INCONSISTENCY: 'Équipements de chauffage',
    MUR_ADJACENT_INCONSISTENCY: 'Isolation mur adjacent',
    MENUISERIES_INCONSISTENCY: 'Qualité des menuiseries',
    MENUISERIES_ETANCHEITE_CLARIFICATION: 'Étanchéité menuiseries',
    ECS_SURDIMENSIONNEMENT: 'Dimensionnement ballon ECS',
    ECS_OPTIMISATION_SURDIMENSIONNEMENT: "Opportunité d'optimisation ECS",
    ECS_SOUS_DIMENSIONNEMENT: 'Dimensionnement ballon ECS',
    ECS_MODE_FONCTIONNEMENT_CLARIFICATION: 'Mode fonctionnement ECS',
  };
  return titles[type] ?? 'Élément du diagnostic';
};

const getCategoryLabel = (category?: string): string => {
  const labels: Record<string, string> = {
    ISOLATION: 'Isolation', CHAUFFAGE: 'Chauffage', VENTILATION: 'Ventilation',
    MENUISERIES: 'Menuiseries', DPE_CORRECTION: 'Correction DPE',
    DPE_OPTIMIZATION: 'Optimisation DPE', REGLEMENTAIRE: 'Réglementaire',
    CONSEIL: 'Conseil', AUDIT: 'Audit',
  };
  return labels[category ?? ''] ?? (category ?? '');
};

const truncate = (str: string, max: number) =>
  str && str.length > max ? str.slice(0, max - 1) + '...' : str;

const formatDateFR = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    paddingTop: 22,
    paddingBottom: 28,
    paddingHorizontal: 26,
    fontSize: 9,
    fontFamily: 'Roboto',
    backgroundColor: '#FFFFFF',
  },

  // ── Header branding ──
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 0,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: '#2563EB',
  },
  headerLeft: { flexDirection: 'column' },
  companyName: { fontSize: 14, fontWeight: 'bold', color: '#1F2937' },
  headerTitle: { fontSize: 9, color: '#6B7280', marginTop: 1 },
  headerAnalyseDate: { fontSize: 8, color: '#9CA3AF', marginTop: 2 },
  logo: { width: 120, height: 56, objectFit: 'contain' },

  // ── Badge lettre DPE ──
  dpeBadge: {
    width: 28, height: 28, borderRadius: 5,
    justifyContent: 'center', alignItems: 'center',
  },
  dpeBadgeText: { fontSize: 15, fontWeight: 'bold' },

  // ── Bandeau identité DPE ──
  dpeBandeau: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 5,
    padding: 8,
    marginTop: 7,
    marginBottom: 8,
  },
  dpeMetaRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 4,
  },
  dpeMetaLabel: {
    fontSize: 8,
    color: '#64748B',
    fontWeight: 'bold',
    marginRight: 4,
    minWidth: 42,
  },
  dpeMetaValue: {
    fontSize: 9,
    color: '#1F2937',
    fontWeight: 'bold',
    flex: 1,
  },
  dpeMetaChip: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    marginRight: 5,
  },
  dpeMetaChipLabel: { fontSize: 6, color: '#94A3B8', marginBottom: 1 },
  dpeMetaChipValue: { fontSize: 7.5, color: '#1E293B', fontWeight: 'bold' },

  // ── Synthèse row ──
  synthesisRow: {
    flexDirection: 'row',
    gap: 7,
    marginBottom: 8,
  },
  synthesisCard: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },

  // ── Score ──
  scoreContainer: { alignItems: 'center', justifyContent: 'center' },
  scoreLabel: { fontSize: 9, fontWeight: 'bold', color: '#1F2937', marginTop: 5, textAlign: 'center' },
  scoreSubLabel: { fontSize: 8, color: '#6B7280', textAlign: 'center', marginTop: 1 },

  // ── Fiabilité / recommandations ──
  infoCardTitle: { flexDirection: 'row', alignItems: 'center', marginBottom: 6, gap: 4 },
  infoCardTitleText: { fontSize: 9, fontWeight: 'bold', color: '#1F2937' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 },
  infoRowLabel: { fontSize: 8.5, color: '#4B5563' },
  infoRowValueOrange: { fontSize: 9, fontWeight: 'bold', color: '#EA580C' },
  infoRowValueBlue: { fontSize: 9, fontWeight: 'bold', color: '#2563EB' },
  infoRowValueGreen: { fontSize: 9, fontWeight: 'bold', color: '#16A34A' },
  divider: { height: 1, backgroundColor: '#E5E7EB', marginVertical: 6 },

  // ── Alert banner ──
  alertBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFF7ED',
    borderWidth: 1.5,
    borderColor: '#FDBA74',
    borderRadius: 5,
    padding: 7,
    marginBottom: 8,
    gap: 6,
  },
  alertBannerTitle: { fontSize: 9, fontWeight: 'bold', color: '#9A3412', marginBottom: 2 },
  alertBannerText: { fontSize: 8, color: '#C2410C', lineHeight: 1.4 },

  // ── Section heading ──
  sectionHeading: {
    fontSize: 9, fontWeight: 'bold', color: '#111827',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2,
  },
  sectionSubheading: { fontSize: 7, color: '#6B7280', marginBottom: 5 },

  // ── Inconsistency card ──
  inconsistencyCard: { borderRadius: 5, borderWidth: 1, marginBottom: 5, overflow: 'hidden' },
  inconsistencyCardHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 8, paddingVertical: 5,
    borderBottomWidth: 1, borderBottomColor: '#E5E7EB',
  },
  inconsistencyCardTitle: { fontSize: 9, fontWeight: 'bold' },
  severityBadge: { paddingHorizontal: 5, paddingVertical: 2, borderRadius: 3 },
  severityBadgeText: { fontSize: 6.5, fontWeight: 'bold' },
  inconsistencyColumns: { flexDirection: 'row' },
  inconsistencyCol: { flex: 1, padding: 6 },
  inconsistencyColBorder: { borderLeftWidth: 1, borderLeftColor: '#E5E7EB' },
  inconsistencyColLabel: { fontSize: 6.5, color: '#9CA3AF', marginBottom: 2, fontWeight: 'bold' },
  inconsistencyColValue: { fontSize: 8.5, color: '#1F2937' },
  inconsistencyReco: {
    paddingHorizontal: 8, paddingVertical: 5,
    borderTopWidth: 1, borderTopColor: '#E5E7EB',
    backgroundColor: '#F8FAFC',
  },
  inconsistencyRecoLabel: { fontSize: 6.5, fontWeight: 'bold', color: '#1E40AF', marginBottom: 1 },
  inconsistencyRecoText: { fontSize: 8, color: '#374151', lineHeight: 1.3 },
  inconsistencyImpactLabel: { fontSize: 6.5, fontWeight: 'bold', color: '#059669', marginTop: 3, marginBottom: 1 },
  inconsistencyImpactText: { fontSize: 8, color: '#374151', lineHeight: 1.3 },

  // ── Work cards grid ──
  worksGrid: { flexDirection: 'row', gap: 5 },
  workCard: {
    flex: 1, backgroundColor: '#FFFFFF', borderRadius: 5,
    borderWidth: 1, borderColor: '#E5E7EB', padding: 7,
  },
  workCardIconRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  workCardTitle: { fontSize: 8.5, fontWeight: 'bold', color: '#1F2937', flex: 1, marginRight: 3 },
  priorityBadge: { paddingHorizontal: 4, paddingVertical: 2, borderRadius: 3 },
  priorityBadgeText: { fontSize: 6, fontWeight: 'bold' },
  workCardDesc: { fontSize: 7.5, color: '#6B7280', lineHeight: 1.35, marginBottom: 5 },
  recommendedBadge: {
    alignSelf: 'flex-end', paddingHorizontal: 5, paddingVertical: 2,
    backgroundColor: '#DCFCE7', borderRadius: 3, marginTop: 'auto',
  },
  recommendedBadgeText: { fontSize: 6, color: '#16A34A', fontWeight: 'bold' },

  // ── Footer ──
  footer: {
    position: 'absolute', bottom: 14, left: 26, right: 26,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: 5, borderTopWidth: 1, borderTopColor: '#E5E7EB',
  },
  footerWatermark: { fontSize: 7, color: '#D1D5DB' },
  footerDisclaimer: { fontSize: 6, color: '#9CA3AF', flex: 1, textAlign: 'center', paddingHorizontal: 8 },
  footerPage: { fontSize: 7, color: '#9CA3AF' },
});

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

const CircularGauge: React.FC<{ value: number; size?: number }> = ({ value, size = 70 }) => {
  const strokeWidth = 7;
  const r = (size - strokeWidth * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const color = value > 80 ? '#10B981' : value > 60 ? '#F59E0B' : '#EF4444';

  const percentage = Math.min(100, Math.max(0, value));
  const angle = (percentage / 100) * 360;
  const startAngle = -90;
  const endAngle = startAngle + angle;

  const startX = cx + r * Math.cos((startAngle * Math.PI) / 180);
  const startY = cy + r * Math.sin((startAngle * Math.PI) / 180);
  const endX = cx + r * Math.cos((endAngle * Math.PI) / 180);
  const endY = cy + r * Math.sin((endAngle * Math.PI) / 180);

  const largeArcFlag = angle > 180 ? 1 : 0;

  const pathData = percentage === 0
    ? ''
    : `M ${startX} ${startY} A ${r} ${r} 0 ${largeArcFlag} 1 ${endX} ${endY}`;

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <Circle
        cx={cx} cy={cy} r={r}
        stroke="#E5E7EB" strokeWidth={strokeWidth} fill="none"
      />
      {percentage > 0 && (
        <Path
          d={pathData}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
        />
      )}
    </Svg>
  );
};

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color, marginTop: 1 }} />
);

// ─────────────────────────────────────────────
// MAIN DOCUMENT
// ─────────────────────────────────────────────

export const AnalysisPDFDocument: React.FC<AnalysisPDFDocumentProps> = ({
  companyName,
  logoUrl,
  primaryColor,
  dpeData,
  confidenceScore,
  rulesAppliedCount = 0,
  inconsistenciesDetected = [],
  immediateActions = [],
  shortTermActions = [],
  collectiveSystemWarning,
  analysisDate,
}) => {
  const reportDate = analysisDate
    ? new Date(analysisDate).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  // ── Champs DPE — sources vérifiées sur la table dpe_data réelle ──
  const energyClass: string =
    dpeData?.etiquette_dpe ??
    dpeData?.energy_class ??
    calculateEtiquetteDPE(
      dpeData?.conso_5_usages_par_m2_ep ??
      dpeData?.primary_energy_consumption ??
      0,
      dpeData?.surface_habitable_logement ?? undefined
    ) ??
    '';

  // adresse_ban = string directe et fiable en base
  // dpeData.address = objet JSON stringifie { street, postalCode, city, ... } - a eviter
  const address: string = dpeData?.adresse_ban ?? 'Adresse non renseignée';

  // Numéro DPE ADEME (ex: "2592E1986727H")
  const numeroDPE: string = dpeData?.numero_dpe ?? '';

  // Dates DPE (distinctes de la date d'analyse)
  const dateDPE: string = formatDateFR(dpeData?.date_etablissement_dpe);
  const dateFinValidite: string = formatDateFR(dpeData?.date_fin_validite_dpe);

  // Version DPE (ex: "2.4")
  const versionDPE: string = dpeData?.version_dpe ?? '';

  // ── Logique d'affichage ──
  const sortedInconsistencies = [...inconsistenciesDetected].sort((a, b) => {
    const order: Record<string, number> = { CRITIQUE: 0, MAJEURE: 1, INFORMATION: 2 };
    return order[a.severity] - order[b.severity];
  });

  const realInconsistencies = inconsistenciesDetected.filter(i => i.severity !== 'INFORMATION');
  const optimizationSuggestions = inconsistenciesDetected.filter(i => i.severity === 'INFORMATION');

  const workRecs = [...immediateActions, ...shortTermActions].filter(rec => {
    if (['DPE_CORRECTION', 'DPE_OPTIMIZATION', 'SYSTEME_COLLECTIF'].includes(rec.category ?? '')) return false;
    if (collectiveSystemWarning?.has_chauffage_collectif && rec.category === 'CHAUFFAGE') return false;
    if (collectiveSystemWarning?.has_ecs_collectif && (rec.id?.includes('ecs') || rec.id?.includes('ballon'))) return false;
    return true;
  }).slice(0, 3);

  const gaugeColor = confidenceScore > 80 ? '#10B981' : confidenceScore > 60 ? '#F59E0B' : '#EF4444';
  const accent = primaryColor ?? '#2563EB';

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* ── HEADER BRANDING ── */}
        <View style={[s.header, { borderBottomColor: accent }]}>
          <View style={s.headerLeft}>
            <Text style={s.companyName}>{companyName || 'DPEWizard'}</Text>
            <Text style={s.headerTitle}>Rapport d'analyse DPE</Text>
            <Text style={s.headerAnalyseDate}>Analyse effectuée le {reportDate}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {logoUrl ? <Image style={s.logo} src={logoUrl} /> : null}
            {energyClass ? (
              <View style={[s.dpeBadge, { backgroundColor: getDPEColor(energyClass) }]}>
                <Text style={[s.dpeBadgeText, { color: getDPETextColor(energyClass) }]}>
                  {energyClass}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* ── BANDEAU IDENTITÉ DPE ── */}
        <View style={s.dpeBandeau}>
          {/* Ligne adresse */}
          <View style={s.dpeMetaRow}>
            <Text style={s.dpeMetaLabel}>Adresse</Text>
            <Text style={s.dpeMetaValue}>{address}</Text>
          </View>
          {/* Chips : numéro DPE + dates + version */}
          <View style={[s.dpeMetaRow, { marginTop: 5 }]}>
            {numeroDPE ? (
              <View style={s.dpeMetaChip}>
                <Text style={s.dpeMetaChipLabel}>N° DPE ADEME</Text>
                <Text style={s.dpeMetaChipValue}>{numeroDPE}</Text>
              </View>
            ) : null}
            {dateDPE ? (
              <View style={s.dpeMetaChip}>
                <Text style={s.dpeMetaChipLabel}>Établi le</Text>
                <Text style={s.dpeMetaChipValue}>{dateDPE}</Text>
              </View>
            ) : null}
            {dateFinValidite ? (
              <View style={s.dpeMetaChip}>
                <Text style={s.dpeMetaChipLabel}>Valide jusqu'au</Text>
                <Text style={s.dpeMetaChipValue}>{dateFinValidite}</Text>
              </View>
            ) : null}
            {versionDPE ? (
              <View style={s.dpeMetaChip}>
                <Text style={s.dpeMetaChipLabel}>Version</Text>
                <Text style={s.dpeMetaChipValue}>DPE v{versionDPE}</Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* ── SYNTHÈSE ── */}
        <View style={s.synthesisRow}>

          {/* Score de confiance */}
          <View style={[s.synthesisCard, s.scoreContainer, { flex: 0.85 }]}>
            <CircularGauge value={confidenceScore} size={70} />
            {/* Valeur numérique en absolu par-dessus le SVG (react-pdf ne supporte pas SVG <text>) */}
            <View style={{
              position: 'absolute',
              alignItems: 'center', justifyContent: 'center',
              top: 10, left: 0, right: 0, height: 70,
            }}>
              <Text style={{ fontSize: 14, fontWeight: 'bold', color: gaugeColor }}>
                {Math.round(confidenceScore)}%
              </Text>
            </View>
            <Text style={s.scoreLabel}>Score de confiance</Text>
            {rulesAppliedCount > 0 && (
              <Text style={s.scoreSubLabel}>{rulesAppliedCount} règles métier</Text>
            )}
          </View>

          {/* Fiabilité + Recommandations */}
          <View style={[s.synthesisCard, { flex: 1.15 }]}>
            <View style={s.infoCardTitle}>
              <Dot color="#3B82F6" />
              <Text style={s.infoCardTitleText}>Fiabilité du DPE</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoRowLabel}>Points d'attention</Text>
              <Text style={s.infoRowValueOrange}>{realInconsistencies.length}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoRowLabel}>Pistes d'amélioration</Text>
              <Text style={[s.infoRowValueBlue, { color: accent }]}>{optimizationSuggestions.length}</Text>
            </View>
            <View style={s.divider} />
            <View style={s.infoCardTitle}>
              <Dot color="#10B981" />
              <Text style={s.infoCardTitleText}>Recommandations travaux</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoRowLabel}>Actions immédiates</Text>
              <Text style={s.infoRowValueGreen}>
                {immediateActions.filter(a => a.category !== 'DPE_CORRECTION' && a.category !== 'REGLEMENTAIRE').length}
              </Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoRowLabel}>Court terme</Text>
              <Text style={s.infoRowValueGreen}>{shortTermActions.length}</Text>
            </View>
          </View>
        </View>

        {/* ── ALERTE INCOHÉRENCES MAJEURES ── */}
        {realInconsistencies.length > 0 && (
          <View style={s.alertBanner}>
            <Text style={{ fontSize: 9, color: '#EA580C' }}>!</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.alertBannerTitle}>DPE à vérifier — Incohérences majeures détectées</Text>
              <Text style={s.alertBannerText}>
                Des écarts entre le DPE officiel et votre situation réelle nécessitent une correction auprès du diagnostiqueur.
              </Text>
            </View>
          </View>
        )}

        {/* ── INCOHÉRENCES DÉTECTÉES ── */}
        {sortedInconsistencies.length > 0 && (
          <View style={{ marginBottom: 8 }}>
            <Text style={s.sectionHeading}>Incohérences détectées</Text>
            <Text style={s.sectionSubheading}>
              {sortedInconsistencies.length} élément{sortedInconsistencies.length > 1 ? 's' : ''} du diagnostic
              {' '}présente{sortedInconsistencies.length > 1 ? 'nt' : ''} des différences avec votre situation réelle
            </Text>

            {sortedInconsistencies.map((inc, i) => {
              const c = getSeverityColor(inc.severity);
              const dpeVal = inc.suspected_error ?? inc.dpe_value ?? '—';
              const userVal = inc.user_reality ?? inc.user_value ?? '—';
              return (
                <View key={i} style={[s.inconsistencyCard, { borderColor: c.border }]}>
                  <View style={[s.inconsistencyCardHeader, { backgroundColor: c.bg }]}>
                    <Text style={[s.inconsistencyCardTitle, { color: c.text }]}>
                      {getInconsistencyTitle(inc.type)}
                    </Text>
                    <View style={[s.severityBadge, { backgroundColor: c.badge }]}>
                      <Text style={[s.severityBadgeText, { color: c.badgeText }]}>
                        {getSeverityLabel(inc.severity)}
                      </Text>
                    </View>
                  </View>

                  <View style={s.inconsistencyColumns}>
                    <View style={s.inconsistencyCol}>
                      <Text style={s.inconsistencyColLabel}>DPE OFFICIEL</Text>
                      <Text style={s.inconsistencyColValue}>{truncate(dpeVal, 90)}</Text>
                    </View>
                    <View style={[s.inconsistencyCol, s.inconsistencyColBorder]}>
                      <Text style={[s.inconsistencyColLabel, { color: '#059669' }]}>VOTRE RÉALITÉ</Text>
                      <Text style={s.inconsistencyColValue}>{truncate(userVal, 90)}</Text>
                    </View>
                  </View>

                  {(inc.recommendation || inc.impact) && (
                    <View style={s.inconsistencyReco}>
                      {inc.recommendation && (
                        <>
                          <Text style={s.inconsistencyRecoLabel}>Recommandation :</Text>
                          <Text style={s.inconsistencyRecoText}>{truncate(inc.recommendation, 200)}</Text>
                        </>
                      )}
                      {inc.impact && (
                        <>
                          <Text style={s.inconsistencyImpactLabel}>Impact :</Text>
                          <Text style={s.inconsistencyImpactText}>{truncate(inc.impact, 160)}</Text>
                        </>
                      )}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* ── TRAVAUX CONSEILLÉS ── */}
        {workRecs.length > 0 && (
          <View style={{ marginBottom: 8 }}>
            <Text style={s.sectionHeading}>Travaux conseillés pour votre logement</Text>
            <Text style={s.sectionSubheading}>
              Recommandations prioritaires basées sur votre DPE et vos réponses au questionnaire
            </Text>
            <View style={s.worksGrid}>
              {workRecs.map((rec, i) => {
                const pc = getPriorityColor(rec.priority);
                return (
                  <View key={i} style={s.workCard}>
                    <View style={s.workCardIconRow}>
                      <Text style={s.workCardTitle}>{truncate(rec.title, 42)}</Text>
                      {rec.priority && (
                        <View style={[s.priorityBadge, { backgroundColor: pc.bg }]}>
                          <Text style={[s.priorityBadgeText, { color: pc.text }]}>{rec.priority}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={s.workCardDesc}>{truncate(rec.description ?? '', 130)}</Text>
                    {rec.category && (
                      <Text style={{ fontSize: 6, color: '#9CA3AF', marginBottom: 4 }}>
                        {getCategoryLabel(rec.category)}
                      </Text>
                    )}
                    <View style={s.recommendedBadge}>
                      <Text style={s.recommendedBadgeText}>Recommandé</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={s.footer} fixed>
          <Text style={s.footerWatermark}>Généré par DPEWizard</Text>
          <Text style={s.footerDisclaimer}>
            Analyse indicative — Les incohérences détectées nécessitent une vérification par un diagnostiqueur certifié.
          </Text>
          <Text style={s.footerPage} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </View>

      </Page>
    </Document>
  );
};

export default AnalysisPDFDocument;